import { useNotification } from '@refinedev/core';
import { Button, Col, DatePicker, Form, Input, Modal, Row, Switch, Typography } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { FC, useCallback, useEffect, useRef } from 'react';

import { AutocompleteCustomerInput } from '../../components/inputs/autocomplete/autocompleteCustomerInput';
import type { Option } from '../../components/inputs/autocomplete/autocomplete';
import { ICustomer } from '../../interfaces/customer';
import { usePrintStatementReport } from '../../api/periodBalances';
import { getErrorMessage } from '../../api/error';

const STATEMENT_DATE_FORMAT = 'DD/MM/YYYY';

function isFilledDate(value: unknown): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  if (dayjs.isDayjs(value)) {
    return value.isValid();
  }
  return dayjs(value as string | number | Date | Dayjs).isValid();
}

export interface StatementReportModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  userId?: number;
  customerLookup?: string;
  startDate: Dayjs;
  endDate: Dayjs;
  showAllBalances: boolean;
}

export const StatementReportModal: FC<StatementReportModalProps> = ({ open, onClose }) => {
  const [form] = Form.useForm<FormValues>();
  const { open: notify } = useNotification();
  const { mutate: printStatementReport, isLoading } = usePrintStatementReport();
  const userId = Form.useWatch('userId', form);
  const hasSelectedUser = userId !== undefined && userId !== null;
  const hadUserRef = useRef(false);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      hadUserRef.current = false;
    }
  }, [open, form]);

  useEffect(() => {
    if (!hasSelectedUser && hadUserRef.current && open) {
      form.setFieldsValue({
        startDate: undefined,
        endDate: undefined,
        showAllBalances: false,
      });
      hadUserRef.current = false;
    }
    if (hasSelectedUser) {
      hadUserRef.current = true;
    }
  }, [hasSelectedUser, open, form]);

  const applyThisMonth = useCallback(() => {
    if (!hasSelectedUser) {
      return;
    }
    const today = dayjs().startOf('day');
    const monthStart = dayjs().startOf('month');
    const monthEnd = dayjs().endOf('month');
    form.setFieldsValue({
      startDate: monthStart,
      endDate: monthEnd.isAfter(today, 'day') ? today : monthEnd,
    });
  }, [form, hasSelectedUser]);

  const applyLastMonth = useCallback(() => {
    if (!hasSelectedUser) {
      return;
    }
    const last = dayjs().subtract(1, 'month');
    form.setFieldsValue({
      startDate: last.startOf('month'),
      endDate: last.endOf('month'),
    });
  }, [form, hasSelectedUser]);

  const handleSubmit = useCallback(async () => {
    if (!hasSelectedUser) {
      return;
    }
    try {
      const values = await form.validateFields();
      const uid = values.userId;
      if (uid === undefined || uid === null) {
        return;
      }
      printStatementReport(
        {
          userId: uid,
          body: {
            startDate: values.startDate.format('YYYY-MM-DD'),
            endDate: values.endDate.format('YYYY-MM-DD'),
            showAllBalances: Boolean(values.showAllBalances),
          },
        },
        {
          onSuccess: (response) => {
            onClose();
            form.resetFields();
            hadUserRef.current = false;
            const url = response.data?.data?.url;
            if (url) {
              window.open(url, '_blank');
            }
          },
          onError: (error: unknown) => {
            const message =
              getErrorMessage(error) ||
              (error as { message?: string })?.message ||
              'Something went wrong!';
            notify?.({
              key: 'print_fail',
              message,
              type: 'error',
            });
          },
        },
      );
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown })?.errorFields) {
        return;
      }
    }
  }, [form, hasSelectedUser, notify, onClose, printStatementReport]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    hadUserRef.current = false;
    onClose();
  }, [form, onClose]);

  const disabledStartDate = useCallback(
    (current: Dayjs | null) => {
      if (!current) {
        return false;
      }
      if (current.isAfter(dayjs(), 'day')) {
        return true;
      }
      const endDate = form.getFieldValue('endDate') as Dayjs | undefined;
      if (endDate) {
        const toValue = dayjs(endDate);
        if (current.isAfter(toValue, 'day')) {
          return true;
        }
        const sixMonthsBeforeToDate = toValue.subtract(6, 'month');
        if (current.isBefore(sixMonthsBeforeToDate, 'day')) {
          return true;
        }
      }
      return false;
    },
    [form],
  );

  const disabledEndDate = useCallback(
    (current: Dayjs | null) => {
      if (!current) {
        return false;
      }
      if (current.isAfter(dayjs(), 'day')) {
        return true;
      }
      const startDate = form.getFieldValue('startDate') as Dayjs | undefined;
      if (startDate) {
        const fromValue = dayjs(startDate);
        if (current.isBefore(fromValue, 'day')) {
          return true;
        }
        const sixMonthsAfterFromDate = fromValue.add(6, 'month');
        if (current.isAfter(sixMonthsAfterFromDate, 'day')) {
          return true;
        }
      }
      return false;
    },
    [form],
  );

  const restLocked = !hasSelectedUser || isLoading;

  return (
    <Modal
      open={open}
      title="Statements & Reports"
      onCancel={handleCancel}
      className="statementReportModal"
      width={680}
      centered
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>,
        <Button
          key="view"
          type="primary"
          loading={isLoading}
          disabled={!hasSelectedUser}
          onClick={handleSubmit}
        >
          View
        </Button>,
      ]}
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        initialValues={{ showAllBalances: false }}
      >
        <Form.Item name="userId" hidden>
          <Input />
        </Form.Item>
        <Form.Item
          label="Customer"
          name="customerLookup"
          style={{ marginBottom: 16 }}
          rules={[
            { required: true, message: 'This field is required' },
            {
              validator: async () => {
                const id = form.getFieldValue('userId');
                if (id === undefined || id === null) {
                  return Promise.reject(new Error('Please select a customer from the list'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <AutocompleteCustomerInput
            disabled={isLoading}
            onSelect={(_value, option: Option<ICustomer>) => {
              const id = option.data.id;
              if (id !== undefined && id !== null) {
                form.setFieldValue('userId', id);
              }
            }}
            onClear={() => {
              form.setFieldValue('userId', undefined);
            }}
            allowClear
            refetchOnEmptyValue
            enableLoadMore
            pageSize={10}
          />
        </Form.Item>

        {!hasSelectedUser && (
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Select a customer to choose dates and generate the statement.
          </Typography.Text>
        )}

        <div className="statement-report-toolbar" style={{ opacity: hasSelectedUser ? 1 : 0.5 }}>
          <div className="statement-report-presets">
            <Button
              type="default"
              className="statement-report-preset-btn"
              onClick={applyThisMonth}
              disabled={restLocked}
            >
              This month
            </Button>
            <Button
              type="default"
              className="statement-report-preset-btn"
              onClick={applyLastMonth}
              disabled={restLocked}
            >
              Last month
            </Button>
          </div>
          <div className="statement-report-balance-toggle">
            <Typography.Text className="statement-report-balance-label">Show all balances</Typography.Text>
            <Form.Item name="showAllBalances" valuePropName="checked" noStyle>
              <Switch disabled={restLocked} />
            </Form.Item>
          </div>
        </div>

        <Row gutter={[20, 0]} className="statement-report-date-row">
          <Col xs={24} sm={12}>
            <Form.Item
              label="From Date"
              name="startDate"
              dependencies={['endDate']}
              rules={[{ required: true, message: 'This field is required' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                className="custom-input-date statement-report"
                format={STATEMENT_DATE_FORMAT}
                placeholder="Select date"
                disabled={restLocked}
                disabledDate={disabledStartDate}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="To Date"
              name="endDate"
              dependencies={['startDate']}
              rules={[
                { required: true, message: 'This field is required' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!isFilledDate(value)) {
                      return Promise.resolve();
                    }
                    const startRaw = getFieldValue('startDate');
                    if (!isFilledDate(startRaw)) {
                      return Promise.resolve();
                    }
                    const end = dayjs.isDayjs(value) ? value : dayjs(value);
                    const start = dayjs.isDayjs(startRaw) ? startRaw : dayjs(startRaw);
                    if (end.isBefore(start, 'day')) {
                      return Promise.reject(new Error('To Date must be on or after From Date'));
                    }
                    const sixMonthsAfter = start.add(6, 'month');
                    if (end.isAfter(sixMonthsAfter, 'day')) {
                      return Promise.reject(new Error('Date range cannot exceed 6 months'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                className="custom-input-date statement-report"
                format={STATEMENT_DATE_FORMAT}
                placeholder="Select date"
                disabled={restLocked}
                disabledDate={disabledEndDate}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
